import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Keyboard, AudioLines, Square, ChevronDown, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from '../context/TranslationContext';
import { applySpokenPunctuation, joinDictation, voiceCommand } from '../utils/spokenPunctuation';

const TEXT_TYPES = ['', 'text'];
const LANG_KEY = 'ivory_dictation_lang';
const LANGS = ['en-US', 'es-US', 'fr-FR', 'pt-BR', 'zh-CN', 'vi-VN', 'fil-PH', 'ko-KR', 'ja-JP', 'hi-IN', 'ur-PK', 'ar-SA', 'fa-IR', 'he-IL', 'ru-RU', 'uk-UA', 'pl-PL', 'de-DE', 'it-IT', 'am-ET', 'sw-KE', 'so-SO', 'yo-NG', 'ig-NG', 'ha-NG', 'zu-ZA', 'th-TH'];
const langLabel = (code) => {
  try { return new Intl.DisplayNames(['en'], { type: 'language' }).of(code.split('-')[0]) || code; } catch { return code; }
};
const SR = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);

const isDictatable = (el) => {
  if (!el || el.disabled || el.readOnly || el.closest('[data-no-dictate]')) return false;
  if (el.tagName === 'TEXTAREA') return true;
  if (el.tagName !== 'INPUT') return false;
  const type = (el.getAttribute('type') || '').toLowerCase();
  const hint = `${el.placeholder || ''} ${el.getAttribute('aria-label') || ''}`.toLowerCase();
  return TEXT_TYPES.includes(type) && el.getAttribute('role') !== 'combobox' && !/search|filter/.test(hint);
};

// Writes into React-controlled fields so the form state updates
const setValue = (el, value) => {
  const proto = el.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(proto, 'value').set.call(el, value);
  el.dispatchEvent(new Event('input', { bubbles: true }));
};

const history = new WeakMap();

const applyDictation = (el, text) => {
  const past = history.get(el) || [];
  const cmd = voiceCommand(text);
  if (cmd?.type === 'discard') return;
  if (cmd?.type === 'undo') {
    if (past.length) { setValue(el, past.pop()); toast.message('Removed the last phrase'); }
    return;
  }
  past.push(el.value);
  history.set(el, past.slice(-30));
  if (cmd?.type === 'clear') { setValue(el, ''); toast.message('Box cleared. Say "undo that" to bring it back'); return; }
  const isArea = el.tagName === 'TEXTAREA';
  setValue(el, joinDictation(el.value, applySpokenPunctuation(text, isArea), isArea));
};

export const DictateOrType = () => {
  const { pathname } = useLocation();
  const { currentLanguage, getSpeechLanguageCode } = useTranslation();
  const [field, setField] = useState(null);
  const [pos, setPos] = useState(null);
  const [listening, setListening] = useState(false);
  const [menu, setMenu] = useState(false);
  const [myLang, setMyLang] = useState(() => localStorage.getItem(LANG_KEY) || '');
  const recRef = useRef(null);
  const active = pathname.startsWith('/dashboard');

  const stop = () => { recRef.current?.stop(); recRef.current = null; setListening(false); };
  const close = () => { stop(); setMenu(false); setField(null); };
  const pickLang = (code) => {
    if (code) localStorage.setItem(LANG_KEY, code); else localStorage.removeItem(LANG_KEY);
    setMyLang(code); setMenu(false);
  };

  useEffect(() => {
    if (!active) return undefined;
    const place = (el) => {
      const r = el.getBoundingClientRect();
      // sit on the field's own corner so the next field stays visible
      const top = r.height >= 64 ? r.bottom - 38 : r.top - 38;
      setPos({ right: Math.max(8, window.innerWidth - r.right + 4), top: top < 4 ? r.bottom + 4 : top });
    };
    const onFocus = (e) => {
      if (!isDictatable(e.target)) return;
      stop(); setMenu(false); setField(e.target); place(e.target);
    };
    const onBlur = (e) => {
      if (e.relatedTarget?.closest?.('[data-dictate-chooser]')) return;
      setTimeout(() => { if (!document.activeElement || !isDictatable(document.activeElement)) close(); }, 0);
    };
    const onKey = (e) => {
      if (recRef.current || !isDictatable(e.target)) return;
      if (e.key.length === 1 || e.key === 'Backspace') setField(null);
    };
    const onMove = () => { const el = document.activeElement; if (isDictatable(el)) place(el); };
    document.addEventListener('focusin', onFocus);
    document.addEventListener('focusout', onBlur);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      document.removeEventListener('focusin', onFocus);
      document.removeEventListener('focusout', onBlur);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => recRef.current?.stop(), []);

  const dictate = () => {
    setMenu(false);
    if (!SR || !field) return;
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = false;
    rec.lang = speechCode;
    rec.onresult = (e) => {
      if (!field.isConnected) { stop(); return; }
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) applyDictation(field, e.results[i][0].transcript.trim());
      }
    };
    rec.onerror = (e) => {
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') toast.error('Microphone access is blocked. Allow it in your browser settings to dictate.');
      else if (e.error === 'audio-capture') toast.error('No microphone found on this device.');
    };
    rec.onend = () => { if (recRef.current === rec) { recRef.current = null; setListening(false); } };
    recRef.current = rec;
    rec.start();
    setListening(true);
    field.focus();
  };

  const appCode = getSpeechLanguageCode(currentLanguage);
  const speechCode = myLang || appCode;
  const langName = langLabel(speechCode);
  if (!active || !field || !pos) return null;
  const keep = (e) => e.preventDefault();
  const btn = 'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors';

  return (
    <div
      data-dictate-chooser
      data-testid="dictate-or-type-chooser"
      onMouseDown={keep}
      style={{ position: 'fixed', right: pos.right, top: pos.top, zIndex: 9999 }}
      className="flex items-center gap-1 p-1 bg-white border border-gray-200 rounded-full shadow-lg"
    >
      {listening ? (
        <button type="button" onClick={stop} className={`${btn} bg-red-500 text-white animate-pulse`} data-testid="dictate-stop-btn">
          <Square className="w-3 h-3" /> Listening in {langName}… tap to stop <span className="font-normal opacity-80">· say "undo that" or "clear box"</span>
        </button>
      ) : (
        <>
          <button type="button" onClick={() => setField(null)} className={`${btn} text-gray-700 hover:bg-gray-100`} data-testid="choose-type-btn">
            <Keyboard className="w-3.5 h-3.5" /> Type
          </button>
          <button
            type="button"
            onClick={dictate}
            disabled={!SR}
            title={SR ? 'Speak to fill this box' : 'Dictation needs Chrome, Edge or Safari'}
            className={`${btn} text-white bg-[#1e40af] hover:bg-[#1e3a8a] disabled:opacity-50`}
            data-testid="choose-dictate-btn"
          >
            <AudioLines className="w-3.5 h-3.5" /> Dictate
          </button>
          <button
            type="button"
            onClick={() => setMenu(m => !m)}
            title="Change dictation language"
            className={`${btn} text-[#1e40af] bg-blue-50 hover:bg-blue-100`}
            data-testid="dictate-language-badge"
          >
            {langName} <ChevronDown className="w-3 h-3" />
          </button>
        </>
      )}
      {menu && !listening && (
        <div
          className="absolute right-0 top-full mt-1 w-52 max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-xl py-1"
          data-testid="dictate-language-menu"
        >
          <p className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-gray-400">Dictation language (only for you)</p>
          {[['', `Same as app (${langLabel(appCode)})`], ...LANGS.map(c => [c, langLabel(c)])].map(([code, label]) => (
            <button
              key={code || 'app'}
              type="button"
              onClick={() => pickLang(code)}
              className="w-full flex items-center justify-between px-3 py-1.5 text-xs text-gray-700 hover:bg-blue-50"
              data-testid={`dictate-lang-${code || 'app'}`}
            >
              {label}
              {myLang === code && <Check className="w-3.5 h-3.5 text-[#1e40af]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
